import React, { useState, useEffect, useRef } from 'react';
import {
  Layers,
  Lock,
  Unlock,
  Link,
  Unlink,
  Plus,
  Minus,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  ArrowUp,
  ArrowDown,
  Sparkles,
  Move,
  Maximize2,
  Combine,
  Split,
  BoxSelect
} from 'lucide-react';
import { VdpElement, Unit } from '../../types';
import { formatUnit, parseUnit } from '../../utils/units';
import { getElementsBoundingBox } from '../../utils/quickExportUtils';
import { MaterialDetailSettingsPanel } from './MaterialDetailSettingsPanel';

interface GroupSelectionControlsProps {
  selectedElements: VdpElement[];
  appUnit: Unit;
  onUpdateElements: (updates: Partial<VdpElement>) => void;
  applyBatchUpdates: (updatesMap: Record<string, Partial<VdpElement>>) => void;
  onApplyPathfinder?: (mode: 'unite' | 'subtract' | 'intersect' | 'exclude' | 'divide' | 'merge') => void;
  onTogglePathfinderPanel?: () => void;
}

export const GroupSelectionControls: React.FC<GroupSelectionControlsProps> = ({
  selectedElements,
  appUnit,
  onUpdateElements,
  applyBatchUpdates,
  onApplyPathfinder,
  onTogglePathfinderPanel
}) => {
  // Current bounding box of selected elements
  const gb = getElementsBoundingBox(selectedElements);

  // Local string states for inputs to prevent premature scaling while typing
  const [localX, setLocalX] = useState<string>(formatUnit(gb.minX, appUnit));
  const [localY, setLocalY] = useState<string>(formatUnit(gb.minY, appUnit));
  const [localW, setLocalW] = useState<string>(formatUnit(gb.width, appUnit));
  const [localH, setLocalH] = useState<string>(formatUnit(gb.height, appUnit));
  const [isRatioLocked, setIsRatioLocked] = useState<boolean>(true);
  const [isFocused, setIsFocused] = useState<boolean>(false);

  // Snapshot refs to avoid compounding scale bugs when editing dimensions
  const baselineBoundsRef = useRef<{ minX: number; minY: number; width: number; height: number }>(gb);
  const baselineElementsRef = useRef<VdpElement[]>(selectedElements);

  // Sync inputs from actual bounding box ONLY when user is not actively typing
  useEffect(() => {
    if (!isFocused) {
      setLocalX(formatUnit(gb.minX, appUnit));
      setLocalY(formatUnit(gb.minY, appUnit));
      setLocalW(formatUnit(gb.width, appUnit));
      setLocalH(formatUnit(gb.height, appUnit));
      baselineBoundsRef.current = gb;
      baselineElementsRef.current = selectedElements;
    }
  }, [selectedElements, appUnit, isFocused]);

  const handleFocus = () => {
    setIsFocused(true);
    baselineBoundsRef.current = getElementsBoundingBox(selectedElements);
    baselineElementsRef.current = [...selectedElements];
  };

  // Commit Group X changes
  const commitGroupX = (targetValueStr?: string) => {
    setIsFocused(false);
    const valToParse = targetValueStr !== undefined ? targetValueStr : localX;
    const newXInPx = parseUnit(valToParse, appUnit);
    if (isNaN(newXInPx)) {
      setLocalX(formatUnit(gb.minX, appUnit));
      return;
    }

    const dx = newXInPx - gb.minX;
    if (Math.abs(dx) < 0.01) return;

    const updatesMap: Record<string, Partial<VdpElement>> = {};
    selectedElements.forEach(el => {
      updatesMap[el.id] = { x: Math.round(el.x + dx) };
    });
    applyBatchUpdates(updatesMap);
  };

  // Commit Group Y changes
  const commitGroupY = (targetValueStr?: string) => {
    setIsFocused(false);
    const valToParse = targetValueStr !== undefined ? targetValueStr : localY;
    const newYInPx = parseUnit(valToParse, appUnit);
    if (isNaN(newYInPx)) {
      setLocalY(formatUnit(gb.minY, appUnit));
      return;
    }

    const dy = newYInPx - gb.minY;
    if (Math.abs(dy) < 0.01) return;

    const updatesMap: Record<string, Partial<VdpElement>> = {};
    selectedElements.forEach(el => {
      updatesMap[el.id] = { y: Math.round(el.y + dy) };
    });
    applyBatchUpdates(updatesMap);
  };

  // Commit Group Width changes (proportional scaling)
  const commitGroupWidth = (targetValueStr?: string) => {
    setIsFocused(false);
    const valToParse = targetValueStr !== undefined ? targetValueStr : localW;
    const newWInPx = parseUnit(valToParse, appUnit);
    const baseW = gb.width;

    if (isNaN(newWInPx) || newWInPx <= 4 || baseW <= 0) {
      setLocalW(formatUnit(gb.width, appUnit));
      return;
    }

    const scaleX = newWInPx / baseW;
    const scaleY = isRatioLocked ? scaleX : 1;

    const updatesMap: Record<string, Partial<VdpElement>> = {};
    selectedElements.forEach(el => {
      const relX = el.x - gb.minX;
      const relY = el.y - gb.minY;
      const elW = el.width || 40;
      const elH = el.height || 40;

      updatesMap[el.id] = {
        x: Math.round(gb.minX + relX * scaleX),
        y: isRatioLocked ? Math.round(gb.minY + relY * scaleY) : el.y,
        width: Math.max(4, Math.round(elW * scaleX)),
        ...(isRatioLocked ? { height: Math.max(4, Math.round(elH * scaleY)) } : {})
      };
    });
    applyBatchUpdates(updatesMap);
  };

  // Commit Group Height changes
  const commitGroupHeight = (targetValueStr?: string) => {
    setIsFocused(false);
    const valToParse = targetValueStr !== undefined ? targetValueStr : localH;
    const newHInPx = parseUnit(valToParse, appUnit);
    const baseH = gb.height;

    if (isNaN(newHInPx) || newHInPx <= 4 || baseH <= 0) {
      setLocalH(formatUnit(gb.height, appUnit));
      return;
    }

    const scaleY = newHInPx / baseH;
    const scaleX = isRatioLocked ? scaleY : 1;

    const updatesMap: Record<string, Partial<VdpElement>> = {};
    selectedElements.forEach(el => {
      const relX = el.x - gb.minX;
      const relY = el.y - gb.minY;
      const elW = el.width || 40;
      const elH = el.height || 40;

      updatesMap[el.id] = {
        y: Math.round(gb.minY + relY * scaleY),
        x: isRatioLocked ? Math.round(gb.minX + relX * scaleX) : el.x,
        height: Math.max(4, Math.round(elH * scaleY)),
        ...(isRatioLocked ? { width: Math.max(4, Math.round(elW * scaleX)) } : {})
      };
    });
    applyBatchUpdates(updatesMap);
  };

  // Step Increment / Decrement Helpers
  const stepValue = (currentVal: string, delta: number) => {
    const num = parseFloat(currentVal);
    if (isNaN(num)) return '0';
    return Math.max(0, num + delta).toFixed(2);
  };

  // Group Alignment Handlers
  const handleAlign = (type: 'left' | 'center' | 'right' | 'top' | 'middle' | 'bottom') => {
    const updatesMap: Record<string, Partial<VdpElement>> = {};

    selectedElements.forEach(el => {
      const elW = el.width || 40;
      const elH = el.height || 40;

      switch (type) {
        case 'left':
          updatesMap[el.id] = { x: gb.minX };
          break;
        case 'center':
          updatesMap[el.id] = { x: Math.round(gb.minX + (gb.width - elW) / 2) };
          break;
        case 'right':
          updatesMap[el.id] = { x: Math.round(gb.minX + gb.width - elW) };
          break;
        case 'top':
          updatesMap[el.id] = { y: gb.minY };
          break;
        case 'middle':
          updatesMap[el.id] = { y: Math.round(gb.minY + (gb.height - elH) / 2) };
          break;
        case 'bottom':
          updatesMap[el.id] = { y: Math.round(gb.minY + gb.height - elH) };
          break;
      }
    });

    applyBatchUpdates(updatesMap);
  };

  const allLocked = selectedElements.every(e => e.locked);
  const handleToggleLockGroup = () => {
    onUpdateElements({ locked: !allLocked });
  };

  // Type summary breakdown
  const typeCounts: Record<string, number> = {};
  selectedElements.forEach(el => {
    const t = el.type.charAt(0).toUpperCase() + el.type.slice(1);
    typeCounts[t] = (typeCounts[t] || 0) + 1;
  });
  const typeSummary = Object.entries(typeCounts).map(([type, count]) => `${count} ${type}`).join(', ');

  // Compute average or mixed opacity
  const firstOpacity = selectedElements[0]?.opacity ?? 1;
  const isUniformOpacity = selectedElements.every(e => (e.opacity ?? 1) === firstOpacity);
  const displayOpacity = isUniformOpacity ? Math.round(firstOpacity * 100) : '';

  return (
    <div className="space-y-3">
      {/* Group Header Card */}
      <div className="bg-zinc-900/90 p-3 rounded-xl border border-blue-500/30 space-y-3 shadow-sm">
        <div className="flex items-center justify-between pb-2 border-b border-zinc-800">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded bg-blue-600/20 text-blue-400 flex items-center justify-center font-bold text-xs shrink-0">
              {selectedElements.length}
            </div>
            <div>
              <h4 className="text-xs font-semibold text-zinc-100 flex items-center gap-1">
                <Layers className="w-3.5 h-3.5 text-blue-400" /> Multi-Element Selection
              </h4>
              <p className="text-[10px] text-zinc-400 truncate max-w-[180px]">{typeSummary}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleToggleLockGroup}
            className={`p-1.5 rounded text-xs flex items-center gap-1 transition-colors cursor-pointer ${
              allLocked 
                ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30 hover:bg-amber-500/30' 
                : 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700'
            }`}
            title={allLocked ? 'Unlock All Selected Elements' : 'Lock All Selected Elements'}
          >
            {allLocked ? <Lock className="w-3.5 h-3.5" /> : <Unlock className="w-3.5 h-3.5" />}
            <span className="text-[10px] font-medium">{allLocked ? 'Locked' : 'Lock All'}</span>
          </button>
        </div>

        {/* Group Position & Bounds Inputs */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[11px] text-zinc-300 font-bold flex items-center gap-1">
              <Move className="w-3 h-3 text-blue-400" />
              <span>Group Position & Bounds</span>
            </span>
            <button
              type="button"
              onClick={() => setIsRatioLocked(!isRatioLocked)}
              className={`flex items-center gap-1 px-1.5 py-0.5 rounded text-[9.5px] font-medium border transition-colors cursor-pointer ${
                isRatioLocked
                  ? 'bg-blue-600/20 border-blue-500/40 text-blue-300'
                  : 'bg-zinc-800 border-zinc-700 text-zinc-400 hover:text-zinc-200'
              }`}
              title={isRatioLocked ? 'Aspect Ratio Locked (Width & Height scale together)' : 'Aspect Ratio Unlocked'}
            >
              {isRatioLocked ? <Link className="w-2.5 h-2.5" /> : <Unlink className="w-2.5 h-2.5" />}
              <span>{isRatioLocked ? 'Locked Ratio' : 'Free Scale'}</span>
            </button>
          </div>

          <div className="grid grid-cols-2 gap-2">
            {/* Group X */}
            <div>
              <label className="text-[10px] text-zinc-400 block mb-0.5">Group X ({appUnit})</label>
              <div className="flex items-center gap-1">
                <input
                  type="text"
                  className="w-full px-2 py-1 border border-zinc-700 rounded text-xs bg-zinc-950 text-zinc-100 focus:ring-1 focus:ring-blue-500 outline-none font-mono"
                  value={localX}
                  onFocus={handleFocus}
                  onChange={e => setLocalX(e.target.value)}
                  onBlur={() => commitGroupX()}
                  onKeyDown={e => {
                    if (e.key === 'Enter') commitGroupX();
                  }}
                />
              </div>
            </div>

            {/* Group Y */}
            <div>
              <label className="text-[10px] text-zinc-400 block mb-0.5">Group Y ({appUnit})</label>
              <div className="flex items-center gap-1">
                <input
                  type="text"
                  className="w-full px-2 py-1 border border-zinc-700 rounded text-xs bg-zinc-950 text-zinc-100 focus:ring-1 focus:ring-blue-500 outline-none font-mono"
                  value={localY}
                  onFocus={handleFocus}
                  onChange={e => setLocalY(e.target.value)}
                  onBlur={() => commitGroupY()}
                  onKeyDown={e => {
                    if (e.key === 'Enter') commitGroupY();
                  }}
                />
              </div>
            </div>

            {/* Group Width */}
            <div>
              <div className="flex items-center justify-between mb-0.5">
                <label className="text-[10px] text-zinc-400">Group Width ({appUnit})</label>
                <div className="flex items-center gap-0.5">
                  <button
                    type="button"
                    onClick={() => {
                      const next = stepValue(localW, -1);
                      setLocalW(next);
                      commitGroupWidth(next);
                    }}
                    className="w-4 h-4 rounded bg-zinc-800 hover:bg-zinc-700 flex items-center justify-center text-zinc-400 hover:text-white cursor-pointer"
                    title="-1 pt"
                  >
                    <Minus className="w-2.5 h-2.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const next = stepValue(localW, 1);
                      setLocalW(next);
                      commitGroupWidth(next);
                    }}
                    className="w-4 h-4 rounded bg-zinc-800 hover:bg-zinc-700 flex items-center justify-center text-zinc-400 hover:text-white cursor-pointer"
                    title="+1 pt"
                  >
                    <Plus className="w-2.5 h-2.5" />
                  </button>
                </div>
              </div>
              <input
                type="text"
                className="w-full px-2 py-1 border border-zinc-700 rounded text-xs bg-zinc-950 text-zinc-100 focus:ring-1 focus:ring-blue-500 outline-none font-mono"
                value={localW}
                onFocus={handleFocus}
                onChange={e => setLocalW(e.target.value)}
                onBlur={() => commitGroupWidth()}
                onKeyDown={e => {
                  if (e.key === 'Enter') commitGroupWidth();
                }}
              />
            </div>

            {/* Group Height */}
            <div>
              <div className="flex items-center justify-between mb-0.5">
                <label className="text-[10px] text-zinc-400">Group Height ({appUnit})</label>
                <div className="flex items-center gap-0.5">
                  <button
                    type="button"
                    onClick={() => {
                      const next = stepValue(localH, -1);
                      setLocalH(next);
                      commitGroupHeight(next);
                    }}
                    className="w-4 h-4 rounded bg-zinc-800 hover:bg-zinc-700 flex items-center justify-center text-zinc-400 hover:text-white cursor-pointer"
                    title="-1 pt"
                  >
                    <Minus className="w-2.5 h-2.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const next = stepValue(localH, 1);
                      setLocalH(next);
                      commitGroupHeight(next);
                    }}
                    className="w-4 h-4 rounded bg-zinc-800 hover:bg-zinc-700 flex items-center justify-center text-zinc-400 hover:text-white cursor-pointer"
                    title="+1 pt"
                  >
                    <Plus className="w-2.5 h-2.5" />
                  </button>
                </div>
              </div>
              <input
                type="text"
                className="w-full px-2 py-1 border border-zinc-700 rounded text-xs bg-zinc-950 text-zinc-100 focus:ring-1 focus:ring-blue-500 outline-none font-mono"
                value={localH}
                onFocus={handleFocus}
                onChange={e => setLocalH(e.target.value)}
                onBlur={() => commitGroupHeight()}
                onKeyDown={e => {
                  if (e.key === 'Enter') commitGroupHeight();
                }}
              />
            </div>
          </div>
        </div>

        {/* Group Opacity Slider & Input */}
        <div>
          <div className="flex items-center justify-between text-[10px] text-zinc-400 mb-1">
            <span>Group Opacity:</span>
            <span className="font-mono text-zinc-200 font-bold">{displayOpacity ? `${displayOpacity}%` : 'Mixed'}</span>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="range"
              min="0"
              max="100"
              value={displayOpacity ? Number(displayOpacity) : 100}
              onChange={e => {
                const pct = parseFloat(e.target.value);
                if (!isNaN(pct)) {
                  onUpdateElements({ opacity: Math.max(0, Math.min(1, pct / 100)) });
                }
              }}
              className="w-full accent-blue-500 cursor-pointer h-1.5 bg-zinc-800 rounded-lg"
            />
            <input
              type="number"
              min="0"
              max="100"
              className="w-14 px-1.5 py-0.5 border border-zinc-700 rounded text-xs bg-zinc-950 text-zinc-100 text-center font-mono"
              placeholder="100"
              value={displayOpacity}
              onChange={e => {
                const pct = parseFloat(e.target.value);
                if (!isNaN(pct)) {
                  onUpdateElements({ opacity: Math.max(0, Math.min(1, pct / 100)) });
                }
              }}
            />
          </div>
        </div>

        {/* Group Alignment Bar */}
        <div className="pt-2 border-t border-zinc-800/80">
          <label className="text-[10px] text-zinc-400 block mb-1 font-medium">Align Elements in Selection:</label>
          <div className="flex items-center justify-between gap-1 bg-zinc-950 p-1 rounded-lg border border-zinc-800">
            <button
              type="button"
              onClick={() => handleAlign('left')}
              className="p-1 hover:bg-zinc-800 text-zinc-400 hover:text-white rounded cursor-pointer"
              title="Align Left"
            >
              <AlignLeft className="w-3.5 h-3.5" />
            </button>
            <button
              type="button"
              onClick={() => handleAlign('center')}
              className="p-1 hover:bg-zinc-800 text-zinc-400 hover:text-white rounded cursor-pointer"
              title="Align Center Horizontally"
            >
              <AlignCenter className="w-3.5 h-3.5" />
            </button>
            <button
              type="button"
              onClick={() => handleAlign('right')}
              className="p-1 hover:bg-zinc-800 text-zinc-400 hover:text-white rounded cursor-pointer"
              title="Align Right"
            >
              <AlignRight className="w-3.5 h-3.5" />
            </button>
            <div className="w-[1px] h-3.5 bg-zinc-800" />
            <button
              type="button"
              onClick={() => handleAlign('top')}
              className="p-1 hover:bg-zinc-800 text-zinc-400 hover:text-white rounded cursor-pointer"
              title="Align Top"
            >
              <ArrowUp className="w-3.5 h-3.5" />
            </button>
            <button
              type="button"
              onClick={() => handleAlign('middle')}
              className="p-1 hover:bg-zinc-800 text-zinc-400 hover:text-white rounded cursor-pointer"
              title="Align Center Vertically"
            >
              <AlignJustify className="w-3.5 h-3.5" />
            </button>
            <button
              type="button"
              onClick={() => handleAlign('bottom')}
              className="p-1 hover:bg-zinc-800 text-zinc-400 hover:text-white rounded cursor-pointer"
              title="Align Bottom"
            >
              <ArrowDown className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        {/* Pathfinder Quick Bar */}
        {onApplyPathfinder && selectedElements.length >= 2 && (
          <div className="pt-2 border-t border-zinc-800/80">
            <div className="flex items-center justify-between text-[10px] text-zinc-400 mb-1">
              <span className="font-semibold text-indigo-400 flex items-center gap-1">
                <Combine className="w-3 h-3" /> Pathfinder Operations:
              </span>
              {onTogglePathfinderPanel && (
                <button
                  type="button"
                  onClick={onTogglePathfinderPanel}
                  className="text-[10px] text-indigo-400 hover:text-indigo-300 hover:underline cursor-pointer font-medium"
                >
                  More...
                </button>
              )}
            </div>
            <div className="grid grid-cols-4 gap-1 bg-zinc-950 p-1 rounded-lg border border-zinc-800">
              <button
                type="button"
                onClick={() => onApplyPathfinder('unite')}
                className="py-1 px-1.5 hover:bg-indigo-600/30 text-indigo-300 hover:text-white rounded text-[10px] font-medium transition-colors flex items-center justify-center gap-1 cursor-pointer"
                title="Unite: Combine all selected shapes"
              >
                <Combine className="w-3 h-3" />
                <span>Unite</span>
              </button>
              <button
                type="button"
                onClick={() => onApplyPathfinder('subtract')}
                className="py-1 px-1.5 hover:bg-rose-600/30 text-rose-300 hover:text-white rounded text-[10px] font-medium transition-colors flex items-center justify-center gap-1 cursor-pointer"
                title="Subtract: Minus Front overlapping shapes"
              >
                <Minus className="w-3 h-3" />
                <span>Subtract</span>
              </button>
              <button
                type="button"
                onClick={() => onApplyPathfinder('intersect')}
                className="py-1 px-1.5 hover:bg-amber-600/30 text-amber-300 hover:text-white rounded text-[10px] font-medium transition-colors flex items-center justify-center gap-1 cursor-pointer"
                title="Intersect: Keep overlapping area"
              >
                <BoxSelect className="w-3 h-3" />
                <span>Intersect</span>
              </button>
              <button
                type="button"
                onClick={() => onApplyPathfinder('exclude')}
                className="py-1 px-1.5 hover:bg-emerald-600/30 text-emerald-300 hover:text-white rounded text-[10px] font-medium transition-colors flex items-center justify-center gap-1 cursor-pointer"
                title="Exclude: XOR overlapping shapes"
              >
                <Split className="w-3 h-3" />
                <span>Exclude</span>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* DETAIL SETTINGS PANEL FOR MATERIAL PROPERTIES & BUMP MAPS ON GROUP */}
      <MaterialDetailSettingsPanel
        selectedElements={selectedElements}
        onUpdateElements={onUpdateElements}
        title="Group Material & Tactile Finishes"
      />
    </div>
  );
};
